/**
 * L1J Web Client - DragGhost
 *
 * 드래그하는 동안 커서를 따라다니는 반투명 아이템 미리보기.
 *
 * **반드시 드래그를 시작하는 컨테이너의 자식으로 붙인다.** GameScene은 UI 전용
 * 카메라를 쓰고 메인 카메라가 무시할 오브젝트를 씬 시작 시 한 번만 모으므로
 * (`registerUiObjects()`), 드래그 중에 씬 최상위로 새 오브젝트를 만들면 그 목록에
 * 들어가지 못해 두 카메라에 이중으로 그려진다. 부모 컨테이너의 자식이면 부모가
 * 이미 받은 카메라 배정을 그대로 물려받는다.
 *
 * 좌표는 부모 컨테이너의 **로컬 좌표**다. 부모가 scrollFactor 0이면
 * `pointer.x - parent.x` 로 바로 환산된다.
 */

import Phaser from 'phaser';
import {
  itemIconKind,
  itemIconTextureKey,
  loadItemIcon,
} from '../../data/itemIcons';

export interface DragGhostItem {
  gfxId?: number;
  /** 표시명. 아이콘 종류 판정의 보조 근거로도 쓰인다. */
  label?: string;
  count?: number;
}

export class DragGhost extends Phaser.GameObjects.Container {
  private bg: Phaser.GameObjects.Rectangle;
  private iconImage: Phaser.GameObjects.Image;
  private iconText: Phaser.GameObjects.Text;
  private countText: Phaser.GameObjects.Text;
  private size: number;

  /**
   * 아이콘 로드가 비동기라, 로드 중에 다른 아이템으로 다시 잡으면 늦게 도착한
   * 텍스처가 엉뚱한 그림을 붙인다. show()마다 증가시켜 뒤늦은 응답을 버린다.
   */
  private token: number = 0;

  constructor(scene: Phaser.Scene, size: number = 36) {
    super(scene, 0, 0);
    this.size = size;

    this.bg = scene.add.rectangle(0, 0, size, size, 0x2a2a3a, 0.85);
    this.bg.setStrokeStyle(2, 0xffd700);
    this.add(this.bg);

    this.iconImage = scene.add.image(0, 0, '__DEFAULT');
    this.iconImage.setVisible(false);
    this.add(this.iconImage);

    this.iconText = scene.add.text(0, 0, '', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    this.iconText.setOrigin(0.5);
    this.add(this.iconText);

    this.countText = scene.add.text(size / 2 - 2, size / 2 - 2, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    this.countText.setOrigin(1, 1);
    this.add(this.countText);

    // 커서 아래에 있으므로 입력을 가로채면 안 된다 (드롭 대상 판정이 어긋난다).
    // Container 자체는 setInteractive를 하지 않으므로 자식만 비활성이면 충분하다.
    this.setAlpha(0.85);
    this.setVisible(false);
    scene.add.existing(this);
  }

  /** 드래그 시작. 좌표는 부모 컨테이너 로컬. */
  public show(item: DragGhostItem, localX: number, localY: number): void {
    const token = ++this.token;

    this.countText.setText(item.count && item.count > 1 ? `x${item.count}` : '');
    this.applyIcon(item, token);

    this.setPosition(localX, localY);
    this.setVisible(true);
  }

  /**
   * 커서를 따라 옮긴다. 이름이 `moveTo`가 아닌 이유는 Phaser Container가
   * 같은 이름의 **자식 재정렬** 메서드를 이미 갖고 있기 때문이다.
   */
  public followTo(localX: number, localY: number): void {
    this.setPosition(localX, localY);
  }

  public hide(): void {
    this.token++;
    this.setVisible(false);
  }

  private applyIcon(item: DragGhostItem, token: number): void {
    const kind = itemIconKind(item.gfxId ?? 0, item.label ?? '');
    const key = itemIconTextureKey(kind);

    if (this.scene.textures.exists(key)) {
      this.showTexture(key);
      return;
    }

    // 아직 없으면 받아 온다. 그 사이에는 폴백 문자를 띄워 칸이 비지 않게 한다.
    this.iconImage.setVisible(false);
    this.iconText.setText('*');
    loadItemIcon(this.scene, kind)
      .then((loadedKey) => {
        if (token !== this.token) return;
        this.showTexture(loadedKey);
      })
      .catch(() => {
        /* 폴백 문자가 이미 떠 있다 */
      });
  }

  private showTexture(key: string): void {
    this.iconImage.setTexture(key);
    // setTexture()는 새 프레임 크기로 width/height를 되돌리므로 매번 다시 잡는다.
    this.iconImage.setDisplaySize(this.size - 8, this.size - 8);
    this.iconImage.setVisible(true);
    this.iconText.setText('');
  }
}
